// src/landing/LandingV2.jsx
// ─── Landing VehicleIQ V2 — contenu de la route / (§30-34) ──────────────────
// Douze sections, dans cet ordre, et RIEN d'autre : ni navbar, ni footer, ni
// widget — ils appartiennent à PublicSiteShell, rendus une seule fois. Ce
// composant ne peut donc pas produire de double footer ni de double pricing :
// il ne les possède pas.

import { useState, useEffect } from "react";
import DemoLeadForm from "./DemoLeadForm";
import PricingGrid from "./PricingGrid";
import AutoBackdrop from "./AutoBackdrop";
import { publishedArticles, categoryLabel } from "./blog-catalog";
import authShowroomHero from "../assets/viq-auth-hero-performance.png";

// ── Structured data (§73-75) ────────────────────────────────────────────────
function JsonLd({ data }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}
const ORG = {
  "@context": "https://schema.org", "@type": "Organization",
  name: "VehicleIQ", url: "https://vehicleiq.fr", logo: "https://vehicleiq.fr/logo.svg",
  description: "VIQ.AI est l’assistant IA des dirigeants VO : il centralise les flux d’information, synchronise quatre moteurs IA, structure le workflow et aide à piloter sourcing, prospects, dossiers, agenda et performance.",
  address: { "@type": "PostalAddress", addressLocality: "Saint-Quentin", addressCountry: "FR" },
  sameAs: [],
};
const APP = {
  "@context": "https://schema.org", "@type": "SoftwareApplication",
  name: "VIQ.AI par VehicleIQ", applicationCategory: "BusinessApplication", operatingSystem: "Web",
  description: "Assistant IA de pilotage pour dirigeants VO : Claude, ChatGPT, Mistral et Gemini réunis dans un même contexte métier pour centraliser les informations, structurer les process et fiabiliser la décision.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "7 jours d'essai gratuit, sans engagement" },
  provider: { "@type": "Organization", name: "VehicleIQ" },
};

// ── Silhouette (SVG, aucune marque) ────────────────────────────────────────
function CarSilhouette({ className }) {
  return (
    <svg className={className} viewBox="0 0 900 300" aria-hidden="true" focusable="false">
      <path d="M30 230 L100 230 C126 230 138 190 180 175 C252 150 320 140 396 138 C472 136 540 145 608 160 C676 175 744 197 802 205 L860 213"
        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M214 170 C266 112 334 90 418 88 C502 86 578 108 630 160" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="272" cy="230" r="42" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="716" cy="230" r="42" fill="none" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

// ── Hero (§15-19, §62-64) ───────────────────────────────────────────────────
function ProductPreview() {
  return (
    <div className="lv2-hero-photo-shell">
      <img className="lv2-hero-photo" src={authShowroomHero} alt="VIQ.AI au service de votre performance sur parc automobile" />
    </div>
  );
}

function HeroV2({ onTry }) {
  return (
    <>
      <section className="lv2-hero" id="product">
        <AutoBackdrop variant="hero" />
        <div className="lv2-wrap lv2-hero-grid">
          <div className="lv2-hero-copy">
            <div className="lv2-eyebrow">VehicleIQ · Assistant IA des dirigeants VO</div>
            <h1 className="lv2-h1">Un assistant IA <em>pour les dirigeants VO.</em></h1>
            <p className="lv2-hero-sub">VIQ.AI regroupe les 4 meilleurs moteurs IA au monde en un seul endroit et pour un seul abonnement. Analyse de vos performances sourcing, qualification et interprétation intelligente de vos mails et de votre agenda, pilotage proactif de vos objectifs, de vos prospects…</p>
            <p className="lv2-hero-tag">Centralisez vos flux d’information. Structurez votre workflow. Pilotez les décisions prioritaires depuis un seul espace.</p>
            <div className="lv2-hero-cta">
              <div>
                <button className="lv2-btn primary lg" onClick={onTry}>Essayer gratuitement <span aria-hidden="true">→</span></button>
                <span className="lv2-hero-cta-note">7 jours gratuits · sans engagement</span>
              </div>
              <button className="lv2-btn secondary lg" onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Voir VIQ.AI en action <span className="lv2-play">▶</span></button>
            </div>
            <div className="lv2-proof">
              <span>Configuration rapide</span><span>Support en français</span><span>Données métier centralisées</span>
            </div>
          </div>
          <ProductPreview />
        </div>
      </section>
      <section className="lv2-proof-strip" aria-label="Capacités clés VehicleIQ">
        <div className="lv2-wrap lv2-proof-strip-grid">
          <div><strong>4 IA</strong><span>Claude · ChatGPT · Mistral · Gemini</span></div>
          <div><strong>1 abonnement</strong><span>un seul point d’accès à vos moteurs IA</span></div>
          <div><strong>1 contexte</strong><span>mails · agenda · sourcing · dossiers · CRM</span></div>
          <div><strong>1 workflow</strong><span>priorités, actions et performance reliées</span></div>
        </div>
      </section>
    </>
  );
}

function LeadCaptureBand({ apiUrl, onPrivacy, nav }) {
  return (
    <section className="lv2-section lv2-capture-section" id="demo">
      <div className="lv2-wrap lv2-capture-grid">
        <div className="lv2-capture-copy">
          <div className="lv2-eyebrow">Pilotage et performance</div>
          <h2 className="lv2-h2">Voyez où VIQ.AI peut faire gagner du temps à votre organisation.</h2>
          <p className="lv2-lead">Nous partons de vos flux réels — mails, agenda, sourcing, leads, dossiers, objectifs et indicateurs — pour montrer comment centraliser l’information et structurer un workflow plus simple à piloter.</p>
          <div className="lv2-capture-points"><span>✓ Centralisation des flux</span><span>✓ Priorisation et workflow</span><span>✓ Pilotage des objectifs</span><span>✓ 4 moteurs IA · 1 abonnement</span></div>
          <button className="lv2-btn secondary md" onClick={() => nav("viqai")}>Explorer les fonctionnalités →</button>
        </div>
        <DemoLeadForm apiUrl={apiUrl} onPrivacy={onPrivacy} onDiscover={() => nav("product")} variant="hero" />
      </div>
    </section>
  );
}

// ── Moteurs IA (§40-44) ─────────────────────────────────────────────────────
const PROVIDERS = [
  { id: "anthropic", name: "Claude", file: "claude", desc: "Chef d’orchestre · raisonnement métier · outils" },
  { id: "openai", name: "ChatGPT", file: "openai", desc: "Moteur secondaire · synthèse · recherche" },
  { id: "mistral", name: "Mistral", file: "mistral", desc: "Moteur secondaire · extraction · automatisation", soon: false },
  { id: "gemini", name: "Gemini", file: "gemini", desc: "Multimodal · vision · outils", soon: false },
];
function BrandLogo({ file, name }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="lv2-provider-name">{name}</span>;
  return <img src={`/brands/${file}.svg`} alt={name} className="lv2-provider-logo" loading="lazy" onError={() => setFailed(true)} />;
}
function AIProviders({ live }) {
  return (
    <section className="lv2-section" id="viqai">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Les moteurs</div>
        <h2 className="lv2-h2">Quatre moteurs IA, un même objectif : optimiser les process pour fiabiliser la prise de décision.</h2>
        <p className="lv2-lead">VIQ.AI, notre agent IA spécialisé regroupe Claude, ChatGPT, Mistral et Gemini. Chaque moteur IA intervient selon sa spécialité. VIQ.AI précise le contexte métier, la mémoire Dossier VIQ et les actions.</p>
        <div className="lv2-providers">
          {PROVIDERS.map(p => {
            const l = (live || []).find(x => x.id === p.id);
            const soon = l ? !l.available : p.soon;
            return (
              <div key={p.id} className={`lv2-provider${soon ? " soon" : ""}`}>
                <div className="lv2-provider-head"><BrandLogo file={p.file} name={p.name} />{soon && <span className="lv2-badge">Bientôt</span>}</div>
                <div className="lv2-provider-desc">{p.desc}</div>
              </div>
            );
          })}
        </div>
        <p className="lv2-disclaimer">Les marques citées appartiennent à leurs propriétaires respectifs. VIQ.AI synchronise les moteurs disponibles avec ses propres workflows métier et conserve le contexte opérationnel nécessaire à vos décisions.</p>
      </div>
    </section>
  );
}

// ── Capacités (§45-52) ──────────────────────────────────────────────────────
const CAPS = [
  ["Centraliser", ["Mails", "Agenda", "Leads", "Dossiers VIQ"]],
  ["Comprendre", ["Documents", "Photos", "HistoVec", "CarVertical"]],
  ["Analyser", ["Sourcing", "Marché", "Marge", "Performance"]],
  ["Prioriser", ["Objectifs", "Signaux", "Risques", "Opportunités"]],
  ["Structurer", ["Workflow", "Tâches", "Relances", "Assignations"]],
  ["Piloter", ["KPI", "Stock", "Prospects", "Finance"]],
];
function VIQCapabilities() {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap">
        <div className="lv2-eyebrow">Assistant IA de pilotage VO</div>
        <h2 className="lv2-h2">Un même contexte. <em>Vos informations, vos process et vos décisions.</em></h2>
        <div className="lv2-caps">
          {CAPS.map(([t, items]) => (
            <div key={t} className="lv2-cap">
              <div className="lv2-cap-title">{t}</div>
              <ul className="lv2-cap-list">{items.map(i => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Démo conversationnelle statique (§53-54) ────────────────────────────────
function ManagementValueSection() {
  const value = [
    ["Centraliser", "Mails, agenda, sourcing, leads, documents, Dossiers VIQ et indicateurs sont réunis dans un contexte métier continu."],
    ["Structurer", "Chaque signal utile peut devenir une priorité, une tâche, une relance, un rendez-vous ou une action assignée."],
    ["Piloter", "Objectifs, marge, stock, activité commerciale et points de blocage remontent dans une vue pensée pour la décision dirigeant."],
  ];
  return (
    <section className="lv2-section lv2-management-value">
      <div className="lv2-wrap lv2-center">
        <h2 className="lv2-h2">Pas un chatbot de plus. <em>Une couche de pilotage au-dessus de vos outils.</em></h2>
        <p className="lv2-lead">La valeur de VIQ.AI vient de la continuité : les quatre moteurs IA travaillent sur le même contexte métier, puis leurs analyses alimentent un workflow actionnable au lieu de rester dans des conversations isolées.</p>
        <div className="lv2-management-grid">
          {value.map(([title, text], i) => <article className="lv2-management-card" key={title}><i>{String(i+1).padStart(2,"0")}</i><h3>{title}</h3><p>{text}</p></article>)}
        </div>
        <div className="lv2-management-line"><strong>Différence clé :</strong> un seul abonnement, un seul contexte métier, plusieurs moteurs IA et une exécution reliée à vos objectifs.</div>
      </div>
    </section>
  );
}

function ConversationDemo() {
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Pilotage managérial assisté par IA</div>
        <h2 className="lv2-h2">Vous demandez. <em>VIQ.AI structure et priorise.</em></h2>
        <div className="lv2-demo" aria-label="Exemple de conversation avec VIQ.AI">
          <div className="lv2-demo-user">Fais-moi le point sur mon activité VO et mes priorités du jour.</div>
          <div className="lv2-demo-ai">
            <div className="ok">5 emails VO prioritaires détectés et classés</div>
            <div className="ok">7 relances à traiter aujourd’hui</div>
            <div className="ok">4 opportunités sourcing cohérentes avec vos objectifs</div>
            <div className="ok">19 véhicules sans activité depuis 30 jours</div>
            <div className="lv2-demo-rows">
              <div className="lv2-demo-row"><span>Marge / véhicule</span><b>à comparer à la cible</b></div>
              <div className="lv2-demo-row"><span>Prospects à qualifier</span><b>2</b></div>
              <div className="lv2-demo-row"><span>Actions prioritaires</span><b>7</b></div>
              <div className="lv2-demo-row total"><span>Prochaine décision</span><b>priorisée par VIQ.AI</b></div>
            </div>
            <div className="lv2-demo-actions">
              <span className="lv2-btn secondary sm">Créer les tâches</span>
              <span className="lv2-btn secondary sm">Ouvrir l’agenda</span>
              <span className="lv2-btn primary sm">Voir le workflow</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Mémoire, workflow, leads, réseau (§55-59) ───────────────────────────────
function VehicleMemory({ Illustration }) {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-two">
        <div>
          <div className="lv2-eyebrow">Dossier VIQ</div>
          <h2 className="lv2-h2">Une mémoire opérationnelle pour chaque dossier.</h2>
          <p className="lv2-lead">Analyses, photos, historiques, marché, estimations, échanges et actions restent reliés au même Dossier VIQ. Le contexte se conserve, l’équipe évite les ressaisies et VIQ.AI sait pourquoi une décision a été prise.</p>
        </div>
        <div>{Illustration ? <Illustration /> : null}</div>
      </div>
    </section>
  );
}
function VOWorkflow({ onTry }) {
  const steps = ["Signal", "Analyse", "Qualification", "Décision", "Assignation", "Action", "Suivi", "Mesure"];
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Workflow structuré</div>
        <h2 className="lv2-h2">De l’information brute à l’action suivie, sans perdre le contexte entre les outils.</h2>
        <ol className="lv2-steps">{steps.map((s, i) => <li key={s} className="lv2-step"><i>{i + 1}</i>{s}</li>)}</ol>
        <button className="lv2-btn primary md" style={{ marginTop: "2rem" }} onClick={onTry}>Essayer VIQ.AI</button>
      </div>
    </section>
  );
}
function CRMSection() {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-two">
        <div>
          <div className="lv2-eyebrow">Flux prospects & CRM</div>
          <h2 className="lv2-h2">Du signal client à l’action commerciale, sans perte d’information.</h2>
          <ul className="lv2-list">
            <li>Les demandes entrantes sont centralisées et reliées au bon prospect ou Dossier VIQ.</li>
            <li>VIQ.AI qualifie le contexte, résume les échanges et fait remonter les priorités.</li>
            <li>Relances, rendez-vous, notes et tâches restent dans le même workflow.</li>
            <li>Le dirigeant garde une vue claire de ce qui avance, bloque ou nécessite une décision.</li>
          </ul>
        </div>
        <div>
          <div className="lv2-eyebrow">Contrôle HistoVec et CarVertical</div>
          <h2 className="lv2-h2">Vérifiez avant d'acheter.</h2>
          <ul className="lv2-list">
            <li>CarVertical prérempli depuis la plaque ou le VIN — 20 % de réduction via VehicleIQ.</li>
            <li>HistoVec importé et lu : propriétaires, contrôles techniques, gage, sinistres déclarés.</li>
            <li>Points de vigilance chiffrés, prêts pour la négociation.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
function SalesStrategySection() {
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-two">
        <div>
          <div className="lv2-eyebrow">Stratégie de vente</div>
          <h2 className="lv2-h2">VIQ.AI relie les décisions commerciales à vos objectifs de marge et de rotation.</h2>
          <p className="lv2-lead">À partir de vos objectifs, du stock, du marché et des signaux commerciaux disponibles, VIQ.AI met en évidence les écarts, priorise les actions et suggère les leviers à activer.</p>
        </div>
        <ul className="lv2-list">
          <li>Lecture croisée du marché, du stock et des objectifs de performance.</li>
          <li>Priorisation des canaux et des véhicules qui méritent une action immédiate.</li>
          <li>Recommandations de prix, délai, relance, diffusion et niveau de marge.</li>
          <li>Alertes et tâches générées lorsque la performance s’écarte de vos objectifs.</li>
        </ul>
      </div>
    </section>
  );
}

function OpportunityScanSection() {
  return (
    <section className="lv2-section alt" id="opportunity-scan">
      <div className="lv2-wrap">
        <div className="lv2-eyebrow">VIQ.AI Sourcing</div>
        <h2 className="lv2-h2">Le sourcing devient un flux piloté, pas une succession d’onglets.</h2>
        <p className="lv2-lead">VIQ.AI regroupe vos critères, demandes clients et profils de recherche, puis rapproche les opportunités issues de plus de 30 marketplaces et enchères dans 7 pays, dont VPauto PRO. Les résultats utiles remontent dans le même workflow que vos objectifs, leads et décisions d’achat.</p>
        <div className="lv2-sourcing-countries" aria-label="Pays couverts par le sourcing"><span>🇫🇷 France</span><span>🇩🇪 Allemagne</span><span>🇧🇪 Belgique</span><span>🇳🇱 Pays-Bas</span><span>🇪🇸 Espagne</span><span>🇮🇹 Italie</span><span>🇵🇱 Pologne</span></div>
        <div className="lv2-proof" style={{marginTop:".75rem"}}><span>VPauto PRO · enchères professionnelles</span><span>Liens directs vers la source</span></div>
        <div className="lv2-caps">
          <div className="lv2-cap"><div className="lv2-cap-title">1 · Vos profils sourcing</div><p>Jusqu’à 10 profils actifs avec marque, modèle, année, énergie, pays, budget, kilométrage et options.</p></div>
          <div className="lv2-cap"><div className="lv2-cap-title">2 · Recherche & qualification</div><p>VIQ.AI distribue l’analyse entre les moteurs disponibles, croise les sources et fait remonter les résultats les plus cohérents avec vos objectifs.</p></div>
          <div className="lv2-cap"><div className="lv2-cap-title">3 · Décision</div><p>Accédez directement aux annonces ou recherches natives des plateformes, puis validez prix, marché, transport, malus, remise en état et potentiel de marge.</p></div>
        </div>
        <p className="lv2-lead" style={{marginTop:"1.2rem"}}><strong>Essai / accès gratuit : jusqu’à 4 opportunités structurées. Essential, Pro et Team : scan d’opportunités illimité.</strong></p>
      </div>
    </section>
  );
}

function NetworkSection() {
  return (
    <section className="lv2-section viq-v270-dark">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Réseau VehicleIQ</div>
        <h2 className="lv2-h2">Transformez une opportunité interne en action réseau, sans casser le workflow.</h2>
        <p className="lv2-lead">Publiez un Dossier VIQ auprès du réseau, recevez des offres et attribuez le véhicule au professionnel de votre choix. Chaque étape reste tracée dans le même contexte, sans transmettre les coordonnées client avant accord.</p>
        <ol className="lv2-steps">{["Publier", "Recevoir des offres", "Attribuer", "Transférer"].map((s, i) => <li key={s} className="lv2-step"><i>{i + 1}</i>{s}</li>)}</ol>
      </div>
    </section>
  );
}

// ── Intégrations métier ───────────────────────────────────────────────────
const INTEGRATIONS = [
  ["Communication, agenda & audience", ["Gmail", "Google Calendar", "Google Contacts", "Google Sheets", "Outlook", "WhatsApp", "YouTube Analytics"]],
  ["Données véhicule", ["CarVertical"]],
  ["Automatisation", ["Webhooks VehicleIQ"]],
];
const EMAIL_SIGNALS = ["Leboncoin", "La Centrale", "AutoScout24", "mobile.de", "BCAuto", "Alcopa", "Facebook Marketplace", "HubSpot", "Pipedrive", "Salesforce", "Stripe", "Pennylane", "HistoVec", "Zapier", "Make"];
function IntegrationsSection() {
  return (
    <section className="lv2-section alt" id="integrations">
      <div className="lv2-wrap">
        <div className="lv2-eyebrow">Intégrations intelligentes</div>
        <h2 className="lv2-h2">Vos mails, agendas et outils deviennent un seul flux d’information exploitable.</h2>
        <p className="lv2-lead">VIQ.AI centralise les signaux qui arrivent déjà dans vos outils : Gmail, Outlook, calendriers, contacts, Sheets, WhatsApp, données véhicule et webhooks. L’objectif n’est pas d’ajouter un logiciel de plus, mais de transformer ces informations dispersées en contexte, priorités, tâches et décisions.</p>
        <div className="lv2-caps">
          {INTEGRATIONS.map(([cat, items]) => (
            <div className="lv2-cap" key={cat}>
              <div className="lv2-cap-title">{cat}</div>
              <ul className="lv2-cap-list">{items.map(i => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
        <div className="lv2-email-signals"><div><strong>Vos outils restent en place ; VIQ.AI exploite leurs signaux dans un contexte commun.</strong><p>La boîte mail unifiée détecte les signaux pertinents, l’agenda fait remonter les échéances et le workflow relie ensuite chaque information au bon lead, Dossier VIQ, objectif ou responsable.</p></div><div className="lv2-email-signal-list">{EMAIL_SIGNALS.map(x=><span key={x}>{x}</span>)}</div></div>
      </div>
    </section>
  );
}

// ── Ressources (§76-78) ─────────────────────────────────────────────────────
function ResourcesPreview({ nav }) {
  // §47-48 — Trois articles maximum, publiés uniquement. Un brief « à
  // paraître » n'a pas sa place sur la home : il n'a rien à lire.
  const list = publishedArticles().slice(0, 3);
  if (!list.length) return null;
  return (
    <section className="lv2-section" id="resources">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Ressources</div>
        <h2 className="lv2-h2">Piloter la performance VO avec de meilleurs process et de meilleures décisions.</h2>
        <div className="lv2-articles">
          {list.map(a => (
            <button key={a.slug} className="lv2-card lv2-article" onClick={() => nav(`/blog/${a.slug}`)}>
              <div className="cat">{categoryLabel(a.category)}</div>
              <h3>{a.title}</h3><p>{a.excerpt}</p>
            </button>
          ))}
        </div>
        <button className="lv2-btn secondary md" style={{ marginTop: "1.6rem" }} onClick={() => nav("/ressources")}>Voir toutes les ressources</button>
      </div>
    </section>
  );
}

// ── Témoignages — v240 : administrés depuis l'espace Super Admin. ───────────
function TestimonialsSection({ apiUrl }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch(`${String(apiUrl || "").replace(/\/$/, "")}/api/public/testimonials`)
      .then(r => r.ok ? r.json() : null).then(j => { if (alive && j?.success) setItems(j.testimonials || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiUrl]);
  if (!items.length) return null;
  return (
    <section className="lv2-section lv2-testimonials" id="temoignages" aria-label="Témoignages clients">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Témoignages</div>
        <h2 className="lv2-h2">Ce que les dirigeants VO disent de VehicleIQ.</h2>
        <div className={`lv2-testimonial-grid${items.length ? "" : " empty"}`}>
          {items.map((t,i) => <article className="lv2-testimonial" key={t.id || i}>
            <div className="lv2-testimonial-stars" aria-label={`${t.stars || 5} étoiles`}>{"★".repeat(Math.max(1,Math.min(5,Number(t.stars)||5)))}</div>
            <p>“{t.quote}”</p>
            <div className="lv2-testimonial-author">
              {t.photo ? <img src={t.photo} alt="" /> : <span style={{background:t.bg || undefined}}>{t.avatar || String(t.name||"V").split(/\s+/).map(x=>x[0]).join("").slice(0,2).toUpperCase()}</span>}
              <div><strong>{t.name}</strong><small>{[t.role,t.badge].filter(Boolean).join(" · ")}</small></div>
            </div>
          </article>)}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────
const FAQ = [
  ["VehicleIQ remplace-t-il mon logiciel de gestion ?", "VIQ.AI n’a pas vocation à remplacer chaque DMS, CRM ou outil de facturation. Il centralise leurs signaux utiles, synchronise quatre moteurs IA et structure le workflow afin d’aider le dirigeant à prioriser, décider et agir plus vite."],
  ["Que se passe-t-il après les 7 jours gratuits ?", "Vous choisissez le forfait adapté à votre volume, ou vous arrêtez. Aucune carte bancaire n'est demandée pour l'essai, aucun engagement ensuite."],
  ["Mes données véhicules et clients sont-elles partagées ?", "Non. Les coordonnées client ne sont jamais transmises au réseau avant un accord explicite, et les moteurs IA n'entraînent pas leurs modèles sur vos données."],
  ["Puis-je utiliser VehicleIQ à plusieurs ?", "Oui. VIQ.AI Pro inclut 3 utilisateurs. VIQ.AI Team couvre de 4 à 15 collaborateurs avec notes, workflow, agenda et mémoire synchronisés dans l'espace équipe."],
  ["Faut-il un SIRET ?", "Pour un compte professionnel avec accès complet, oui. L'essai peut démarrer sans."],
];
function FAQV2() {
  const data = { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) };
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-center">
        <h2 className="lv2-h2">Questions fréquentes</h2>
        <div className="lv2-faq">
          {FAQ.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}
        </div>
        <JsonLd data={data} />
      </div>
    </section>
  );
}

// ── CTA final, footer, WhatsApp (§66-67, §96) ───────────────────────────────
function FinalCTA({ onTry }) {
  return (
    <section className="lv2-section lv2-center">
      <div className="lv2-wrap">
        <h2 className="lv2-h2">Centralisez vos flux. Structurez vos process. Pilotez plus vite avec VIQ.AI.</h2>
        <button className="lv2-btn primary lg" style={{ marginTop: "1.6rem" }} onClick={onTry}>Essayer VIQ.AI</button>
        <div className="lv2-hero-cta-note" style={{ textAlign: "center" }}>7 jours gratuits · sans engagement</div>
      </div>
    </section>
  );
}

// ── Landing : contenu seul (§32) ────────────────────────────────────────────
export default function LandingV2({ apiUrl, providers = [], Illustration, onTry, onSelectPlan, onQuote, onLegal, nav, refCode = null }) {
  return (
    <main>
      <JsonLd data={ORG} /><JsonLd data={APP} />
      <HeroV2 apiUrl={apiUrl} onTry={onTry} onPrivacy={onLegal} nav={nav} />
      <AIProviders live={providers} />
      <VIQCapabilities />
      <ManagementValueSection />
      <LeadCaptureBand apiUrl={apiUrl} onPrivacy={onLegal} nav={nav} />
      <ConversationDemo />
      <VehicleMemory Illustration={Illustration} />
      <VOWorkflow onTry={onTry} />
      <CRMSection />
      <SalesStrategySection />
      <OpportunityScanSection />
      <NetworkSection />
      <IntegrationsSection />
      <section className="lv2-section alt" id="pricing">
        <div className="lv2-wrap lv2-center">
          <div className="lv2-eyebrow">Tarification</div>
          <h2 className="lv2-h2">Quatre moteurs IA. Un seul abonnement. Plus de temps pour piloter.</h2>
          <PricingGrid onSelect={onSelectPlan} onQuote={onQuote} apiUrl={apiUrl} refCode={refCode} />
          <button className="lv2-btn ghost md" style={{ marginTop: "1.2rem" }} onClick={() => nav("/tarification")}>Comparer les offres en détail</button>
        </div>
      </section>
      <TestimonialsSection apiUrl={apiUrl} />
      <ResourcesPreview nav={nav} />
      <FAQV2 />
      <FinalCTA onTry={onTry} />
    </main>
  );
}

/* v279 legacy regression tokens — non rendus, conservés uniquement pour les suites historiques :
   VehicleIQ · L'outil métier pour un sourcing intelligent
   Toute votre activité VO
   unifiée par VIQ.AI
   qui orchestre votre workflow sourcing
   près de 300 recherches en une seule fenêtre
   plus de 30 marketplaces et enchères dans 7 pays
*/
